import { tokenManager } from './tokenManager';

/**
 * Wrapper de alto orden para servicios API con manejo automático de tokens
 * 
 * @description Implementa el patrón Decorator para agregar funcionalidad de
 * regeneración automática de tokens a cualquier función de API. Maneja
 * transparentemente errores 401 (Unauthorized), regenera tokens expirados
 * y reintenta peticiones automáticamente sin intervención del desarrollador.
 * 
 * @function withTokenRetry
 * 
 * @param {Function} apiCall - Función de API que requiere autenticación
 * @param {Function} apiCall.args - Argumentos de la función API original
 * @param {string} apiCall.token - Token de autenticación (agregado automáticamente)
 * 
 * @returns {Function} Función envuelta con capacidad de reintento automático
 * @returns {Promise<Object>} Resultado de la API con estructura:
 *   - statusCode: Código HTTP de respuesta
 *   - data: Datos de respuesta o array vacío en caso de error
 *   - error?: Mensaje de error en caso de fallo del servicio
 * 
 * @throws {Error} Solo para errores no relacionados con autenticación
 * 
 * @example
 * // Envolver servicio de búsqueda
 * const _getSearch = async (query, page, size, category, token) => {
 *   // Lógica original del servicio
 * };
 * 
 * export const getSearch = withTokenRetry(_getSearch);
 * 
 * @example
 * // Uso del servicio envuelto (sin token manual)
 * const results = await getSearch('query', 0, 20, 'category');
 * // El wrapper maneja automáticamente:
 * // 1. Obtiene token válido
 * // 2. Ejecuta la petición
 * // 3. Si recibe 401, regenera token y reintenta
 * 
 * @example
 * // Envolver múltiples servicios
 * export const getSearch = withTokenRetry(_getSearch);
 * export const getSuggestions = withTokenRetry(_getSuggestions);
 * export const getCategories = withTokenRetry(_getCategories);
 * 
 * @example
 * // Manejo de errores del wrapper
 * try {
 *   const result = await wrappedApiCall(params);
 *   
 *   switch (result.statusCode) {
 *     case 200:
 *       console.log('Éxito:', result.data);
 *       break;
 *     case 503:
 *       console.error('Servicio no disponible:', result.error);
 *       break;
 *     default:
 *       console.warn('Respuesta inesperada:', result.statusCode);
 *   }
 * } catch (error) {
 *   console.error('Error crítico:', error);
 * }
 * 
 * @since 1.0.0
 * @author Valeria Garcia
 * 
 * @see {@link ./tokenManager.js} Gestor de tokens utilizado internamente
 * @see {@link ./getSearch.js} Ejemplo de servicio que usa este wrapper
 * @see {@link ./getSuggestions.js} Ejemplo de servicio que usa este wrapper
 */
export const withTokenRetry = (apiCall) => {
  /**
   * Función envuelta con capacidad de reintento automático de tokens
   * 
   * @async
   * @function wrappedApiCall
   * @inner
   * 
   * @param {...*} args - Argumentos originales de la función API
   * 
   * @returns {Promise<Object>} Resultado de la API con manejo de errores:
   *   - statusCode 200: Operación exitosa
   *   - statusCode 503: Servicio de autenticación no disponible
   *   - statusCode original: Para otros códigos de respuesta
   * 
   * @description Implementa el flujo completo de manejo de tokens:
   * 
   * **Flujo Normal:**
   * 1. Obtiene token válido del TokenManager
   * 2. Ejecuta la función API original con el token
   * 3. Retorna el resultado si es exitoso
   * 
   * **Flujo de Recuperación (401):**
   * 1. Detecta token expirado (statusCode 401)
   * 2. Solicita regeneración de token al TokenManager
   * 3. Reintenta la petición con el nuevo token
   * 4. Retorna el resultado del reintento
   * 
   * **Flujo de Error (Servicio no disponible):**
   * 1. Si falla la regeneración de token
   * 2. Retorna error 503 con mensaje descriptivo
   * 3. Permite recuperación automática cuando el servicio vuelva
   * 
   * @example
   * // El wrapper transforma esto:
   * const result = await apiCall(param1, param2, token);
   * 
   * // En esto (automático):
   * const token = await tokenManager.getValidToken();
   * const result = await apiCall(param1, param2, token);
   * if (result.statusCode === 401) {
   *   const newToken = await tokenManager.handleUnauthorized();
   *   return await apiCall(param1, param2, newToken);
   * }
   */
  return async (...args) => {
    try {
      // Primer intento con token actual
      const token = await tokenManager.getValidToken();
      const result = await apiCall(...args, token);
      
      // Manejo automático de tokens expirados (401 Unauthorized)
      if (result?.statusCode === 401) {
        console.log('Token expirado, regenerando...');
        
        try {
          // Token expirado, regenerar y reintentar automáticamente
          const newToken = await tokenManager.handleUnauthorized();
          
          console.log('Nuevo token obtenido, reintentando petición...');
          
          // Reintentar la petición original con el nuevo token
          const retryResult = await apiCall(...args, newToken);
          
          console.log('Resultado del reintento:', retryResult?.statusCode);
          
          return retryResult;
        } catch (tokenError) {
          console.error('Error al regenerar token:', tokenError);
          
          // Si falla la regeneración de token, retornar error de servicio no disponible
          // Esto permite que la aplicación maneje gracefully la indisponibilidad temporal
          return {
            statusCode: 503, // Service Unavailable
            data: [],
            error: 'Servicio de autenticación no disponible. Inténtalo más tarde.'
          };
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error in API call with token retry:', error);
      
      // Manejo específico de errores del servicio de tokens
      if (error.message?.includes('Token service unavailable')) {
        return {
          statusCode: 503,
          data: [],
          error: 'Servicio no disponible temporalmente'
        };
      }
      
      // Para otros errores críticos (red, timeout, etc.), relanzar
      // Esto permite que el código cliente maneje errores específicos
      throw error;
    }
  };
};